import React, { useEffect, useState } from 'react';
import videoCacheService from '../utils/videoCacheService';

/**
 * CachedVideo Component
 *
 * A video component that automatically uses cached videos when available
 * Falls back to original URL if caching fails
 */
const CachedVideo = ({
  src,
  className = '',
  autoPlay = true,
  muted = true,
  loop = true,
  playsInline = true,
  preload = 'auto',
  poster,
  onLoadedData,
  style,
  ...otherProps
}) => {
  const [videoSrc, setVideoSrc] = useState(src);
  const [isLoading, setIsLoading] = useState(true);
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    let mounted = true;
    let objectUrl = null;

    const loadVideo = async () => {
      try {
        // Initialize cache service
        if (!videoCacheService.isInitialized) {
          await videoCacheService.init();
        }

        // Get cached video blob
        const blob = await videoCacheService.getVideo(src);

        if (!mounted) return;

        if (blob) {
          // Create object URL from blob
          objectUrl = URL.createObjectURL(blob);
          setBlobUrl(objectUrl);
          setVideoSrc(objectUrl);
        } else {
          // Fallback to original URL
          setVideoSrc(src);
        }
      } catch (error) {
        console.error('Failed to load cached video:', error);
        if (mounted) {
          setVideoSrc(src); // Fallback to original URL
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    loadVideo();

    // Cleanup: revoke blob URL on unmount
    return () => {
      mounted = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [src]);

  return (
    <video
      className={className}
      src={videoSrc}
      autoPlay={autoPlay}
      muted={muted}
      loop={loop}
      playsInline={playsInline}
      preload={preload}
      poster={poster}
      onLoadedData={onLoadedData}
      style={style}
      {...otherProps}
    />
  );
};

export default CachedVideo;
